// packages import
import { FC } from 'react'
import { Box, TableBody, TableCell, TableHead, TableRow, styled, Typography } from '@mui/material'
import { format } from 'date-fns'

// component import
import { CustomPagination } from '../../common/CustomPagination'
import { StickyTable } from '../../common/StickyTable'
import { TableSkeleton } from '../../skeletons/TableSkeleton'

// others
import { enumToLabel, formatCurrency } from '../../../utils'
import { Link } from 'react-router-dom'
import palette from '../../../theme/palette'
import { AccountsReceivableListingProps } from '../../../interfaceTypes/accountsReceivable'
import { EmptyData } from '../../common/EmptyData'
import { flexCenter } from '../../../theme/styleConstants'
import { splitBillsByClient } from '../../../utils/accountReceivables'

const CustomLink = styled(Link)(() => ({
  color: palette.primary.main,
  textDecoration: 'underline'
}))

const PaymentContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column'
}))

const PaymentAmount = styled(Typography)(() => ({
  fontSize: '15px'
}))

const PaymentDate = styled(Typography)(() => ({
  fontSize: '12px',
  color: '#666666'
}))

export const AccountsReceivableListing: FC<AccountsReceivableListingProps> = ({
  arEntries,
  loading,
  refetch
}): JSX.Element => {
  if (!loading && arEntries?.length === 0) {
    return (
      <Box sx={{ ...flexCenter, height: 'calc(100vh - 400px)' }}>
        <EmptyData title="No accounts receivable found" />
      </Box>
    )
  }

  return (
    <StickyTable pagination={<CustomPagination loading={loading || false} />} height="500px">
      <TableHead>
        <TableRow>
          <TableCell> Invoice </TableCell>
          <TableCell> Issue Date </TableCell>
          <TableCell> Due Date </TableCell>
          <TableCell> Client </TableCell>
          <TableCell> Matter </TableCell>
          <TableCell> Total </TableCell>
          <TableCell> Written Off </TableCell>
          <TableCell> Paid </TableCell>
          <TableCell> Balance </TableCell>
          <TableCell> Last Payment </TableCell>
        </TableRow>
      </TableHead>

      {loading ? (
        <TableSkeleton noOfRows={7} noOfCols={9} />
      ) : (
        <TableBody>
          {splitBillsByClient(arEntries)?.map(entry => {
            const { bill, case: litigation, client, recentPaymentAllocation, total, paid, balance, writtenOff } = entry

            const lastPaymentDate = recentPaymentAllocation
              ? format(new Date(recentPaymentAllocation.paymentDate), 'MM/dd/yyyy')
              : ''

            return (
              <TableRow hover key={`${bill?.id}_${client?.id}`}>
                <TableCell>
                  <CustomLink to={`/billing/bill-detail/${bill?.id}?clientId=${client?.id}`}>
                    {bill?.id && `INV-${bill.id}`}
                  </CustomLink>
                </TableCell>
                <TableCell>{format(new Date(bill?.issueDate), 'MM/dd/yyyy')}</TableCell>
                <TableCell>{bill?.dueDate ? format(new Date(bill?.dueDate), 'MM/dd/yyyy') : ''}</TableCell>

                <TableCell>
                  {client?.firstName || ''} {client?.lastName || ''} - {enumToLabel(client?.caseRole || '')}
                </TableCell>

                <TableCell>
                  <CustomLink to={`/matters/matter-detail/${litigation?.id}`}>{`#${
                    litigation?.caseNumber || litigation?.consultMatterNumber
                  }`}</CustomLink>
                </TableCell>

                <TableCell>{formatCurrency(total || 0)}</TableCell>
                <TableCell>{formatCurrency(writtenOff || 0)}</TableCell>
                <TableCell>{formatCurrency(paid || 0)}</TableCell>
                <TableCell>{formatCurrency(balance || 0)}</TableCell>
                <TableCell>
                  {recentPaymentAllocation ? (
                    <PaymentContainer>
                      <PaymentAmount>{formatCurrency(recentPaymentAllocation.amount || 0)}</PaymentAmount>
                      <PaymentDate>{lastPaymentDate}</PaymentDate>
                    </PaymentContainer>
                  ) : (
                    <></>
                  )}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      )}
    </StickyTable>
  )
}
